import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Home, ChevronRight, ChevronLeft, FileText, Calendar, Eye, Scale } from "lucide-react";
import { useIsMobile } from "../components/ui/use-mobile";
import { Radio, Input } from "antd";

interface PolicyItem {
  id: number;
  information_title: string;
  information_content: string;
  tag: string;
  policy_level: string;
  release_time: string;
  create_time: string;
}

export default function PolicyListPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const [policies, setPolicies] = useState<PolicyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState<string>('全部');
  const [keyword, setKeyword] = useState<string>('');
  const pageSize = 6;

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const resp = await fetch('/api/public/information/policy');
      const data = await resp.json();
      if (data.ok) {
        setPolicies(data.data || []);
      }
    } catch (err) {
      console.error('获取政策法规列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const normalize = (s: string) => (s || '').toLowerCase();
  const stripHtml = (html: string) => (html || '').replace(/<[^>]+>/g, '');
  const keywordNormalized = normalize(keyword.trim());

  // 根据选择的分级 + 关键字筛选数据
  const filteredPolicies = (selectedLevel === '全部'
    ? policies
    : policies.filter(p => p.policy_level === selectedLevel)
  ).filter(p => {
    if (!keywordNormalized) return true;
    const title = normalize(p.information_title);
    const content = normalize(stripHtml(p.information_content));
    const tag = normalize(p.tag);
    return title.includes(keywordNormalized) || content.includes(keywordNormalized) || tag.includes(keywordNormalized);
  });

  const totalPages = Math.ceil(filteredPolicies.length / pageSize);
  const paginatedData = filteredPolicies.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // 当筛选条件改变时，重置到第一页
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedLevel, keywordNormalized]);

  // 从富文本中提取纯文本摘要
  const extractSummary = (html: string, maxLength: number = 150) => {
    if (!html) return '';
    const text = html.replace(/<[^>]+>/g, '');
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  // 格式化日期时间
  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).replace(/\//g, '-');
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header />

      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #003c8c 0%, #0050b8 50%, #0066cc 100%)',
        padding: isMobile ? '32px 0' : '60px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰元素 */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30%',
          left: '-5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        }} />
        
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', marginBottom: isMobile ? '12px' : '16px' }}>
            <Scale style={{ width: isMobile ? '28px' : '40px', height: isMobile ? '28px' : '40px', color: '#fbbf24' }} />
            <h1 style={{ 
              fontSize: isMobile ? '24px' : '36px', 
              fontWeight: 700, 
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              政策法规
            </h1>
          </div>
          <p style={{ fontSize: isMobile ? '14px' : '16px', color: 'rgba(255,255,255,0.8)' }}>
            汇集最新政策文件，解读法规动态
          </p>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '12px 0' : '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', fontSize: isMobile ? '12px' : '14px', flexWrap: 'wrap' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none' }}>
              <Home style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <span style={{ color: '#003c8c', fontWeight: 500 }}>政策法规</span>
          </nav>
        </div>
      </div>

      {/* 分级筛选 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '16px 0' : '20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row' }}>
            <Radio.Group
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              buttonStyle="solid"
              style={{ width: isMobile ? '100%' : 'auto' }}
            >
              <Radio.Button value="全部" style={{
                minWidth: isMobile ? '60px' : '80px',
                textAlign: 'center',
                borderRadius: isMobile ? '6px' : '8px'
              }}>全部</Radio.Button>
              <Radio.Button value="国家级" style={{
                minWidth: isMobile ? '60px' : '80px',
                textAlign: 'center',
                borderRadius: isMobile ? '6px' : '8px'
              }}>国家级</Radio.Button>
              <Radio.Button value="省级" style={{
                minWidth: isMobile ? '60px' : '80px',
                textAlign: 'center',
                borderRadius: isMobile ? '6px' : '8px'
              }}>省级</Radio.Button>
              <Radio.Button value="市县级" style={{
                minWidth: isMobile ? '60px' : '80px',
                textAlign: 'center',
                borderRadius: isMobile ? '6px' : '8px'
              }}>市县级</Radio.Button>
            </Radio.Group>

            <Input
              allowClear
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="关键字搜索"
              style={{
                width: isMobile ? '100%' : 320,
                borderRadius: isMobile ? 8 : 10
              }}
            />
          </div>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '20px 0' : '40px 0' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
            加载中...
          </div>
        ) : filteredPolicies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
            {keywordNormalized
              ? `暂无包含“${keyword.trim()}”的文章`
              : (selectedLevel === '全部' ? '暂无政策法规文章' : `暂无${selectedLevel}政策法规文章`)
            }
          </div>
        ) : (
          <>
            {/* 文章列表 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {paginatedData.map((item) => (
                <div
                  key={item.id}
                  onClick={() => navigate(`/policy/${item.id}`)}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: isMobile ? '12px' : '12px',
                    padding: isMobile ? '16px' : '28px 32px',
                    cursor: 'pointer',
                    boxShadow: hoveredItem === item.id 
                      ? '0 10px 40px rgba(0,60,140,0.15)' 
                      : '0 2px 8px rgba(0,0,0,0.06)',
                    transform: hoveredItem === item.id ? 'translateY(-4px)' : 'translateY(0)',
                    transition: 'all 0.3s ease',
                    borderLeft: hoveredItem === item.id ? '4px solid #003c8c' : '4px solid transparent'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? '12px' : '20px' }}>
                    {/* 图标 */}
                    <div style={{
                      width: isMobile ? '48px' : '56px',
                      height: isMobile ? '48px' : '56px',
                      borderRadius: isMobile ? '10px' : '12px',
                      background: hoveredItem === item.id 
                        ? 'linear-gradient(135deg, #003c8c, #0050b8)' 
                        : '#f0f7ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.3s ease'
                    }}>
                      <FileText style={{ 
                        width: isMobile ? '24px' : '28px', 
                        height: isMobile ? '24px' : '28px', 
                        color: hoveredItem === item.id ? 'white' : '#003c8c' 
                      }} />
                    </div>

                    {/* 内容 */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', marginBottom: isMobile ? '8px' : '10px', flexWrap: 'wrap' }}>
                        {item.policy_level && (() => {
                          let backgroundColor = '#dbeafe';
                          let color = '#2563eb';
                          if (item.policy_level === '国家级') {
                            backgroundColor = '#fee2e2';
                            color = '#dc2626';
                          } else if (item.policy_level === '省级') {
                            backgroundColor = '#dbeafe';
                            color = '#2563eb';
                          } else if (item.policy_level === '市县级') {
                            backgroundColor = '#d1fae5';
                            color = '#059669';
                          }
                          return (
                            <span style={{
                              padding: isMobile ? '3px 10px' : '4px 12px',
                              backgroundColor,
                              color,
                              fontSize: isMobile ? '11px' : '12px',
                              fontWeight: 600,
                              borderRadius: '20px'
                            }}>
                              {item.policy_level}
                            </span>
                          );
                        })()}
                        {item.tag && (
                          <span style={{
                            padding: isMobile ? '3px 10px' : '4px 12px',
                            backgroundColor: '#fef3c7',
                            color: '#d97706',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: 600,
                            borderRadius: '20px'
                          }}>
                            {item.tag}
                          </span>
                        )}
                      </div>
                      
                      <h3 style={{
                        fontSize: isMobile ? '16px' : '18px',
                        fontWeight: 600,
                        color: hoveredItem === item.id ? '#003c8c' : '#1f2937',
                        marginBottom: isMobile ? '8px' : '10px',
                        lineHeight: 1.5,
                        transition: 'color 0.2s'
                      }}>
                        {item.information_title}
                      </h3>
                      
                      <p style={{
                        fontSize: isMobile ? '13px' : '14px',
                        color: '#6b7280',
                        lineHeight: 1.7,
                        marginBottom: isMobile ? '12px' : '16px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {extractSummary(item.information_content)}
                      </p>

                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '16px' : '24px', fontSize: isMobile ? '12px' : '13px', color: '#9ca3af' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Calendar style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px' }} />
                          {formatDateTime(item.release_time)}
                        </span>
                      </div>
                    </div>

                    {/* 箭头 */}
                    {!isMobile && (
                      <ChevronRight style={{
                        width: '24px',
                        height: '24px',
                        color: hoveredItem === item.id ? '#003c8c' : '#d1d5db',
                        transition: 'all 0.2s',
                        transform: hoveredItem === item.id ? 'translateX(4px)' : 'translateX(0)'
                      }} />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '6px' : '8px',
                marginTop: isMobile ? '32px' : '48px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: isMobile ? '8px 16px' : '10px 20px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: currentPage === 1 ? '#d1d5db' : '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <ChevronLeft style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                  上一页
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      width: isMobile ? '32px' : '40px',
                      height: isMobile ? '32px' : '40px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: page === currentPage ? '#003c8c' : 'white',
                      color: page === currentPage ? 'white' : '#374151',
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: page === currentPage ? 600 : 400,
                      cursor: 'pointer',
                      boxShadow: page === currentPage ? '0 4px 12px rgba(0,60,140,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s'
                    }}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: isMobile ? '8px 16px' : '10px 20px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: currentPage === totalPages ? '#d1d5db' : '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  下一页
                  <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
